import { addDays, startOfDay } from 'date-fns';

/**
 * Removes a trailing `_YYYY-MM-DD` occurrence suffix appended by
 * `expandRecurringEvents`, returning the base event uid. Plain uids and uids
 * whose underscores are not date segments are returned unchanged.
 */
export function stripOccurrenceDateSuffix(uid: string): string {
  return uid.replace(/_\d{4}-\d{2}-\d{2}$/, '');
}

/**
 * Returns the trailing `YYYY-MM-DD` occurrence suffix of an expanded recurring
 * event uid, or null if the uid has no such suffix. Inverse view of
 * {@link stripOccurrenceDateSuffix}.
 */
export function extractOccurrenceDateSuffix(uid: string): string | null {
  const match = uid.match(/_(\d{4}-\d{2}-\d{2})$/);
  return match ? match[1] : null;
}

/** A time range with a start and end Date. */
export interface TimeRange {
  start: Date;
  end: Date;
}

/**
 * Round `minutes` to the nearest `step` boundary.
 *
 * @example
 *   snapMinutes(37)     // 30  (15-min grid)
 *   snapMinutes(38)     // 45  (rounds up to next 15)
 *   snapMinutes(37, 30) // 30  (30-min grid)
 */
export function snapMinutes(minutes: number, step = 15): number {
  return Math.round(minutes / step) * step;
}

/**
 * Convert a pixel delta to a minute delta.
 * Returns 0 when `pixelsPerMinute` is zero or negative (guard against division by zero).
 *
 * @param deltaPx - Distance moved in pixels
 * @param pixelsPerMinute - Scale factor (pixels per minute)
 */
export function pxToMinutes(deltaPx: number, pixelsPerMinute: number): number {
  if (pixelsPerMinute <= 0) {
    return 0;
  }
  return deltaPx / pixelsPerMinute;
}

/**
 * Compute new start/end after a drag-move operation.
 *
 * The slot duration is preserved. The new start is snapped to `step` minutes,
 * then clamped so the slot stays fully within [00:00, 24:00] of the same day.
 * Input Date objects are never mutated.
 *
 * @param start - Current slot start
 * @param end - Current slot end
 * @param deltaMinutes - Raw minute delta from the drag
 * @param step - Snap grid in minutes (default 15)
 */
export function computeMovedTimes(
  start: Date,
  end: Date,
  deltaMinutes: number,
  step = 15
): TimeRange {
  const dayStart = startOfDay(start);
  const durationMin = (end.getTime() - start.getTime()) / 60_000;
  const startMin = (start.getTime() - dayStart.getTime()) / 60_000;

  const rawNewStartMin = snapMinutes(startMin + deltaMinutes, step);
  // Clamp so the slot fits fully inside [0, 1440].
  const clampedStartMin = Math.max(0, Math.min(rawNewStartMin, 1440 - durationMin));

  const newStart = new Date(dayStart.getTime() + clampedStartMin * 60_000);
  const newEnd = new Date(newStart.getTime() + durationMin * 60_000);

  return { start: newStart, end: newEnd };
}

/**
 * Compute new start/end after a drag-resize operation.
 *
 * For edge `'start'`: moves the start (end stays fixed), snaps to `step`,
 *   and clamps into [00:00, end - minDurationMin].
 * For edge `'end'`: moves the end (start stays fixed), snaps to `step`,
 *   and clamps into [start + minDurationMin, 24:00].
 *
 * Minimum duration is enforced (default 15 minutes).
 * Input Date objects are never mutated.
 *
 * @param start - Current slot start
 * @param end - Current slot end
 * @param edge - Which edge is being dragged: `'start'` or `'end'`
 * @param deltaMinutes - Raw minute delta from the drag
 * @param step - Snap grid in minutes (default 15)
 * @param minDurationMin - Minimum allowed duration in minutes (default 15)
 */
export function computeResizedTimes(
  start: Date,
  end: Date,
  edge: 'start' | 'end',
  deltaMinutes: number,
  step = 15,
  minDurationMin = 15
): TimeRange {
  const dayStart = startOfDay(start);
  const startMin = (start.getTime() - dayStart.getTime()) / 60_000;
  const endMin = (end.getTime() - dayStart.getTime()) / 60_000;

  if (edge === 'start') {
    const rawNewStartMin = snapMinutes(startMin + deltaMinutes, step);
    // Clamp: cannot go before day start, cannot push start past (end - minDuration).
    const clampedStartMin = Math.max(0, Math.min(rawNewStartMin, endMin - minDurationMin));
    const newStart = new Date(dayStart.getTime() + clampedStartMin * 60_000);
    return { start: newStart, end: new Date(end) };
  }

  // edge === 'end'
  const rawNewEndMin = snapMinutes(endMin + deltaMinutes, step);
  // Clamp: cannot go past midnight, cannot pull end before (start + minDuration).
  const clampedEndMin = Math.max(startMin + minDurationMin, Math.min(rawNewEndMin, 1440));
  const newEnd = new Date(dayStart.getTime() + clampedEndMin * 60_000);
  return { start: new Date(start), end: newEnd };
}

/**
 * Compute new start/end after a cross-day move.
 *
 * The slot is shifted by `dayDelta` whole days and by `minuteDelta` minutes
 * (snapped to `step`). Duration is preserved. Unlike `computeMovedTimes`, the
 * result is NOT clamped to a single day, so the slot may cross midnight and
 * render on the previous/next day. Input Date objects are never mutated.
 */
export function computeMovedTimesAcrossDays(
  start: Date,
  end: Date,
  dayDelta: number,
  minuteDelta: number,
  step = 15
): TimeRange {
  const durationMin = (end.getTime() - start.getTime()) / 60_000;
  const dayStart = startOfDay(start);
  const startMin = (start.getTime() - dayStart.getTime()) / 60_000;
  const baseDay = addDays(dayStart, dayDelta);
  const newStartMin = snapMinutes(startMin + minuteDelta, step);

  const newStart = new Date(baseDay.getTime() + newStartMin * 60_000);
  const newEnd = new Date(newStart.getTime() + durationMin * 60_000);

  return { start: newStart, end: newEnd };
}

/**
 * Compute new start/end after resizing one edge to an absolute (day, minute)
 * target. `targetMinutes` is expected within [0, 1440] (the column height), so
 * a vertical drag stays within the day; spanning days happens by targeting an
 * adjacent column's `targetDate`. Min duration is enforced. Inputs are never
 * mutated.
 *
 * Dragging an edge past the opposite edge (e.g. the end edge onto a day before
 * start) does not invert the range; it clamps to the minimum duration on the
 * fixed edge's day.
 */
export function computeResizedTimesToTarget(
  start: Date,
  end: Date,
  edge: 'start' | 'end',
  targetDate: Date,
  targetMinutes: number,
  step = 15,
  minDurationMin = 15
): TimeRange {
  const dayStart = startOfDay(targetDate);
  const snapped = snapMinutes(targetMinutes, step);
  const targetTime = dayStart.getTime() + snapped * 60_000;

  if (edge === 'end') {
    const minEndTime = start.getTime() + minDurationMin * 60_000;
    return {
      start: new Date(start),
      end: new Date(Math.max(targetTime, minEndTime))
    };
  }

  // edge === 'start'
  const maxStartTime = end.getTime() - minDurationMin * 60_000;
  return {
    start: new Date(Math.min(targetTime, maxStartTime)),
    end: new Date(end)
  };
}
